import { modApi } from "../api";
import { REPORT_CATEGORIES, type Report } from "../types";
import { confirmAction } from "../ui/ConfirmDialog";
import { escapeHtml, formatDateTime } from "./format";

/**
 * Card de uma denuncia, comum ao painel do jogo e ao /admin. Os botoes levam
 * data-report-close="resolved|dismissed" e data-report-id; quem renderiza liga
 * os cliques com bindReportButtons.
 */
export function reportHtml(r: Report, options: { actions: boolean; targetLabel?: string }): string {
  const time = (ms: number) => new Date(ms).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const evidence = r.evidence.length
    ? `<ul class="report-evidence">${r.evidence
        .map((l) => `<li><span class="audit-when">${time(l.at)}</span> ${escapeHtml(l.text)}</li>`)
        .join("")}</ul>`
    : `<p class="report-none">Sem falas recentes no chat</p>`;
  const handled =
    r.status === "open"
      ? ""
      : `<p class="report-handled">${r.status === "resolved" ? "Resolvida" : "Descartada"} por ${escapeHtml(r.handledBy ?? "?")}
         ${r.handledAt ? `em ${formatDateTime(r.handledAt)}` : ""}${r.note ? ` — ${escapeHtml(r.note)}` : ""}</p>`;
  const buttons =
    options.actions && r.status === "open"
      ? `<div class="mod-actions">
           <button type="button" class="secondary small" data-report-close="resolved" data-report-id="${r.id}">Resolvida</button>
           <button type="button" class="secondary small" data-report-close="dismissed" data-report-id="${r.id}">Descartar</button>
           ${r.targetId ? `<button type="button" class="secondary small" data-report-target="${r.targetId}">${options.targetLabel ?? "Ver jogador"}</button>` : ""}
         </div>`
      : "";
  return `
    <li class="report">
      <div class="report-head">
        <span class="badge warn">${REPORT_CATEGORIES[r.category]}</span>
        <strong>${escapeHtml(r.targetName)}</strong>${r.targetId ? "" : ` <span class="mod-handle">(conta excluida)</span>`}
        <span class="mod-handle">denunciado por ${escapeHtml(r.reporterName)} · ${formatDateTime(r.createdAt)}</span>
      </div>
      ${r.details ? `<p class="report-details">“${escapeHtml(r.details)}”</p>` : ""}
      <div class="report-label">Últimas falas no chat</div>
      ${evidence}
      ${handled}
      ${buttons}
    </li>`;
}

/** Liga os botoes de fechar denuncia; chama onDone com a mensagem de sucesso. */
export function bindReportButtons(root: HTMLElement, token: string, reports: Report[], onDone: (message: string) => void): void {
  root.querySelectorAll<HTMLButtonElement>("[data-report-close]").forEach((button) =>
    button.addEventListener("click", async () => {
      const report = reports.find((r) => r.id === Number(button.dataset.reportId));
      if (!report) return;
      const status = button.dataset.reportClose as "resolved" | "dismissed";
      const result = await confirmAction({
        title: status === "resolved" ? "Marcar como resolvida" : "Descartar denúncia",
        message:
          status === "resolved"
            ? `A denúncia contra ${report.targetName} sai da fila como resolvida. Aplique a punição (se houver) antes ou depois, pelas ações do jogador.`
            : `A denúncia contra ${report.targetName} sai da fila sem punição.`,
        confirmLabel: status === "resolved" ? "Resolvida" : "Descartar",
        fields: [{ name: "note", label: "Observação (opcional, só a moderação vê)", type: "textarea", maxLength: 200 }],
        run: (v) => modApi.closeReport(token, report.id, status, v.note).then(() => "Denúncia atualizada."),
      });
      if (result !== null) onDone(result);
    }),
  );
}
